import type {
  MachineId, MetaVerb, RunPhase, SetzenPayload, SimEvent, SimInput, Snapshot, Tag, TokenSpec,
} from '../shared/contract.js';
import { DEBT_CURVE, VERDACHT_THRESHOLDS } from './economy.js';
import { ITEMS, itemsOfKind } from './items/catalogue.js';
import { stolenCharm, ZINKEN } from './items/house.js';
import { makeProphecies, type Prophecy } from './items/wahrsager.js';
import { HAUSREGELN, type HausregelDef } from './hausregeln.js';
import { applyBeat, isLehrBeat, LEHRNACHT_RULES, type LehrHost } from './lehrnacht.js';
import type { Modifier } from './modifier.js';
import type { PrizeStock } from './machines/greifer.js';
import { Sim, type SimOptions } from './sim.js';
import { DT } from './const.js';

/**
 * THE RUN — floors, the House, the shop, Die Abrechnung, the receipt.
 *
 * This is the layer core/ deliberately did not build in wave 1 (MASCHINENBAU cut 3), and
 * it is written under one hard constraint: **a run is still exactly (seed + input log)**.
 * Every decision the House makes, every shop stock draw, every ZINKEN placement, every
 * floor's rule set comes out of a labelled RNG stream on the same `Sim` — never out of a
 * wall clock, never out of module state. Two runs of the same seed and inputs produce the
 * same receipt, which is what makes the daily seed and the FOTOAUTOMAT possible later.
 *
 * The other constraint is the covenant's cousin: **the House gets no privileged code
 * path.** Its riggings are `Modifier`s from the same registry your charms come from, its
 * bouncer shoves the cabinet with the same `ruetteln()` you use, and its Saalchef's rules
 * are modifiers you can win and keep. There is no `if (isHouse)` anywhere below.
 */

/**
 * **DECISION: six floors, not eight.** core-loop.md leaves this OPEN ("floor count 6 vs 8
 * decided by M2 playtest data") and the agent brief asks for six. Six with the same ×2.2
 * curve keeps the property that actually matters — a linear-value build dies at floor 4–5
 * — while landing the run at 40–50 minutes instead of 60+, which is the browser-session
 * worry the same OPEN raises. The curve's last two entries (8.000 / 20.000) are kept in
 * economy.ts for when the count is revisited.
 */
export const FLOORS = 6;

/** Tickets buy the Saalchef door; the quota rises with the floor but not exponentially —
 *  it rewards play *quality*, while the debt curve taxes wealth (economy.md). */
export function ticketQuotaFor(floor: number): number {
  return 12 + 10 * (floor - 1);
}

export interface RunOptions extends Omit<SimOptions, 'machines' | 'floor'> {
  seed: number;
  floors?: number;
  /**
   * Restrict the run's item pool to these ids (the meta layer's unlock ladder). Omitted
   * or empty means "everything in the catalogue", which is what every test and every
   * headless bot wants. **Unlocks add ids to this list and nothing else** — no unlock may
   * ever change a number, because runs have to stay equal for the daily seed to mean
   * anything (core-loop.md).
   */
  pool?: readonly string[];
  /**
   * **DIE LEHRNACHT.** A new profile's first run is the tutorial, and the tutorial is a
   * real run of this same director — one floor, a fixed seed, a fifty-coin marker, and a
   * shell-side director that gates the player's hands beat by beat. See `lehrnacht.ts`.
   *
   * All this flag does *here* is stop the House from furnishing floor 1 the usual way:
   * no rules are drawn (the board must be empty so the first line to appear on it is the
   * one the player earned), and the Preistheke is not stocked (beat 3 stocks it, with
   * exactly one parcel). Everything else the Lehrnacht changes arrives as a `lehrbeat`
   * input, so it is in the log and the whole night replays.
   */
  lehrnacht?: boolean;
}

/** What Der Croupier remembers. The receipt is a render of this. */
interface Chronicle {
  bestCombo: { value: number; tags: Tag[]; machine: MachineId; pocket: string } | null;
  dumbestLoss: { value: number; machine: MachineId; what: string } | null;
  verdachtPeak: number;
  confiscated: string[];
  grabbed: string[];
  bankBreaks: number;
  floorsCleared: number;
  /** the floor on which Der Croupier first took something instead of being paid */
  firstConfiscationFloor: number;
  stakes: number;
  totalWon: number;
  /** the meta layer's unlock ladder reads these; none of them changes the run */
  bossesBeaten: number;
  shoves: number;
  shovesThisFloor: number;
  cleanFloors: number;
  bestPayout: number;
  maxTagsOnToken: number;
  zinkenStolen: number;
}

interface BossPhase {
  rule: Modifier;
  target: number;
}

const ALL_MACHINES: readonly MachineId[] = ['pachinko', 'muenzschieber', 'bandit', 'roulette', 'greifer'];

export class RunDirector implements LehrHost {
  readonly sim: Sim;
  readonly floors: number;
  /** THE REPLAY. A run is this array plus the seed. Nothing else. */
  readonly inputs: SimInput[] = [];

  private pool: string[] = [];
  /** the unlocked subset of the catalogue, or null for "all of it" */
  private allowed: Set<string> | null = null;
  /** tonight is the tutorial; see `RunOptions.lehrnacht` and `core/lehrnacht.ts` */
  private readonly lehrnacht: boolean = false;
  private installedZinken: string[] = [];
  private floorRules: HausregelDef[] = [];
  private bag: string[] = [];
  private chronicle: Chronicle = {
    bestCombo: null, dumbestLoss: null, verdachtPeak: 0, confiscated: [],
    grabbed: [], bankBreaks: 0, floorsCleared: 0, firstConfiscationFloor: -1,
    stakes: 0, totalWon: 0,
    bossesBeaten: 0, shoves: 0, shovesThisFloor: 0, cleanFloors: 0,
    bestPayout: 0, maxTagsOnToken: 0, zinkenStolen: 0,
  };
  private bossPhases: BossPhase[] = [];
  private bossPhase = -1;
  /** the gauntlet runs while you play cabinets, so it cannot key off `sim.phase`. */
  private bossActive = false;
  private bossBanked = 0;
  private bossDeadline = -1;
  /** the Saalchef's own GLÜCK pool, spent against you */
  private bossGlueck = 0;
  /** next tick he puts one of his own tokens on the table */
  private bossNextBag = 0;
  private bouncer: { machine: MachineId; nextShove: number; survived: number } | null = null;
  private receiptLines: string[] = [];
  /** what Der Croupier himself took at the last Abrechnung — see `abrechnung()` */
  private lastSettlement: string[] = [];
  private lastCoins = 0;
  private foto: import('./sim.js').CabinetSnapshot | null = null;
  /**
   * Tonight's prophecies, built fresh for this run. See items/wahrsager.ts: two of them
   * remember things, and a remembering modifier at module scope is the twin-run
   * determinism trap MASCHINENBAU documented.
   */
  private readonly prophecies: Prophecy[] = makeProphecies();
  /** the three on offer at this floor's WAHRSAGER, and which have been bought */
  private wahrsagerCard: number[] = [];
  private boughtProphecies: string[] = [];

  static async create(opts: RunOptions): Promise<RunDirector> {
    // The FLIPPER is built with the rest, but it is not an aisle cabinet: it stands
    // behind the Saalchef's door and only `openBoss()` ever switches it on.
    const sim = await Sim.create({
      ...opts, machines: [...ALL_MACHINES, 'flipper'], floor: 1, seedPile: true,
    });
    return new RunDirector(sim, opts.floors ?? FLOORS, opts.pool, opts.lehrnacht === true);
  }

  constructor(sim: Sim, floors = FLOORS, pool?: readonly string[], lehrnacht = false) {
    this.sim = sim;
    this.floors = floors;
    this.allowed = pool && pool.length > 0 ? new Set(pool) : null;
    this.lehrnacht = lehrnacht;
    sim.onMeta = (m) => this.meta(m);
    // exactly once, ever — see Sim.emit for why this is not a peekEvents() loop
    sim.observer = (e) => this.readEvent(e);
    if (lehrnacht) for (const r of LEHRNACHT_RULES) sim.pipe.add(r);
    this.buildPool();
    this.startFloor(1);
  }

  // ------------------------------------------------------------------ replay surface

  /**
   * The one door inputs come through. The shell calls this; the tick is stamped here so
   * the log can never disagree with the sim about *when* something happened.
   */
  push(verb: SimInput['verb'], machine: MachineId, payload?: SimInput['payload']): void {
    const i: SimInput = { tick: this.sim.tick, verb, machine, payload };
    this.inputs.push(i);
    // An "Einsatz" on the receipt is a stake YOU made. Counting `drop` events instead
    // would count every Der-Deckel re-entry as a fresh bet and report six-figure nonsense.
    if (verb === 'setzen') this.chronicle.stakes++;
    this.sim.applyInput(i);
  }

  /** Replay an existing log. Same seed + same log = same run, bit for bit. */
  replay(inputs: readonly SimInput[], untilTick: number): void {
    const queue = [...inputs].sort((a, b) => a.tick - b.tick);
    let qi = 0;
    while (this.sim.tick < untilTick) {
      while (qi < queue.length && queue[qi]!.tick <= this.sim.tick) {
        const i = queue[qi++]!;
        this.inputs.push(i);
        this.sim.applyInput(i);
      }
      this.step();
    }
  }

  step(): void {
    this.sim.step();
    this.watch();
  }

  snapshot(): Snapshot {
    return this.sim.snapshot();
  }

  // ------------------------------------------------------------------ variation engine

  /**
   * The replay-variety engine (brief priority 6). Runs must diverge *structurally*, and
   * the four levers are all here: the item pool is drawn **without replacement** per run,
   * the floor's Hausregeln force different machine priorities, ZINKEN pressure redirects
   * routes, and the shop's stock is whatever the claw's physical pile happens to hold.
   */
  private buildPool(): void {
    const ids = [
      ...itemsOfKind('enchant').map((d) => d.id),
      ...itemsOfKind('charm').map((d) => d.id),
      ...itemsOfKind('manipulation').map((d) => d.id),
    ].sort().filter((id) => !this.allowed || this.allowed.has(id));
    // Fisher-Yates on a labelled stream: the pool order IS the run's shape, so it must be
    // reproducible and it must not consume draws any other system depends on.
    const out = [...ids];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(this.sim.rng.float('run:pool') * (i + 1));
      const tmp = out[i]!;
      out[i] = out[j]!;
      out[j] = tmp;
    }
    this.pool = out;
  }

  /** Draw n items from the run pool, without replacement. An empty pool stays empty. */
  private drawPool(n: number): string[] {
    return this.pool.splice(0, Math.max(0, n));
  }

  private startFloor(floor: number): void {
    const sim = this.sim;
    sim.economy.floor = floor;
    sim.ticketQuota = ticketQuotaFor(floor);
    sim.phase = 'aisle';

    // ---- fresh Hausregeln. Two per floor from a pool, drawn without replacement within
    // the floor, and each one skews which machines are worth walking to.
    for (const r of this.floorRules) sim.pipe.remove(r.modifier.id);
    this.floorRules = [];
    // DIE LEHRNACHT starts with a blank board on purpose. The lesson of beat 3 is that
    // items rewrite games, and it is told by a line appearing on a board that had nothing
    // on it — which cannot be told if the House has already written two rules there.
    const avail = this.lehrnacht ? [] : HAUSREGELN.filter((r) => r.minFloor <= floor);
    const picks = new Set<number>();
    const want = Math.min(2, avail.length);
    let guard = 0;
    while (picks.size < want && guard++ < 40) {
      picks.add(Math.floor(sim.rng.float('floor:rules') * avail.length));
    }
    for (const i of [...picks].sort((a, b) => a - b)) {
      const rule = avail[i]!;
      this.floorRules.push(rule);
      sim.pipe.add(rule.modifier);
    }

    // ---- which cabinets are switched on. The Preistheke always is (machines.md: one per
    // floor), and build denial is soft difficulty, so the House darkens one or two.
    const dark = new Set<MachineId>();
    const darkCount = floor <= 1 ? 0 : 1 + (sim.rng.float('floor:power') < 0.35 ? 1 : 0);
    const candidates: MachineId[] = ['pachinko', 'muenzschieber', 'bandit', 'roulette'];
    for (let k = 0; k < darkCount; k++) {
      const pick = candidates[Math.floor(sim.rng.float('floor:power') * candidates.length)]!;
      if (dark.size < candidates.length - 2) dark.add(pick);
    }
    for (const id of ALL_MACHINES) {
      const c = sim.cabinet(id);
      if (c) c.powered = !dark.has(id);
    }

    // ---- Der Croupier wheels your cabinets downstairs. They travel WITH you, so this is
    // a snapshot round-trip rather than a rebuild: the pusher pile keeps its shape, your
    // sticky stalagmites persist, cracked glass stays cracked (core-loop.md).
    if (floor > 1) this.travel();

    // ---- the wonder-machine slot (core-loop.md: at most one per floor). The FOTOAUTOMAT
    // is the one built: it snapshots a cabinet's full physical state and redeems the
    // photograph later to restore it. Bank a perfect pile before a risky session, or undo
    // a disaster. It is also, deliberately, the same round-trip the floor transition and
    // the save file use — architecture.md's "one mechanism, two features", now three.
    // core-loop.md: at most one wonder machine per floor. Two are built now — the
    // FOTOAUTOMAT (snapshot/restore, which is the save system wearing a hat) and DER
    // WAHRSAGER (prophecies that make themselves true). Which one turns up is a draw.
    const wunderRoll = sim.rng.float('floor:wunder');
    this.sim.wunder = floor >= 2 && wunderRoll < 0.62
      ? (wunderRoll < 0.31 ? 'fotoautomat' : 'wahrsager')
      : null;
    if (this.sim.wunder !== 'fotoautomat') this.sim.fotoHeld = null;
    if (this.sim.wunder === 'wahrsager') this.dealWahrsager();
    else this.wahrsagerCard = [];
    sim.wunderCard = this.wahrsagerCardText();

    // ---- restock the claw with whatever the run pool still holds. On the Lehrnacht the
    // Preistheke is dark and empty until beat 3 puts exactly one parcel on its floor.
    if (!this.lehrnacht) this.restockShop(floor);

    sim.emit({ t: 'floorStart', floor });
  }

  /**
   * Cabinet snapshots (MASCHINENBAU cut 4, and architecture.md's "one mechanism, two
   * features"): the same round-trip is the floor transition, the save file and the
   * FOTOAUTOMAT. Taking it and immediately restoring it on every floor change means the
   * mechanism is exercised every single run rather than only when someone saves.
   */
  private travel(): void {
    for (const id of ALL_MACHINES) {
      const snap = this.sim.snapshotCabinet(id);
      if (snap) this.sim.restoreCabinet(id, snap);
    }
  }

  /** Test seam: walk a whole run's shop offerings without playing the run. */
  restockShopForTest(floor: number): void {
    this.restockShop(floor);
  }

  private restockShop(floor: number): void {
    const g = this.sim.greifer();
    if (!g) return;
    const n = 3 + (floor >= 3 ? 1 : 0);
    const ids = this.drawPool(n);
    const stock: PrizeStock[] = ids.map((id) => {
      const def = ITEMS[id];
      return { item: id, name: def ? def.name : id, worth: def?.rarity === 'rar' ? 40 : 20 };
    });
    // The bear is a physics puzzle, not a purchase: he shows up, he does not fit the
    // chute, and getting him out is the joke. One per run, from floor 2.
    if (floor >= 2 && this.sim.rng.float('shop:baer') < 0.45 && !this.bag.includes('der-riesenplueschbaer')) {
      const baer = ITEMS['der-riesenplueschbaer'];
      stock.push({ item: 'der-riesenplueschbaer', name: baer ? baer.name : 'Der Riesenplüschbär', big: true, worth: 90 });
    }
    if (this.sim.rng.float('shop:brecheisen') < 0.4 && this.installedZinken.length > 0) {
      stock.push({ item: 'item:brecheisen', name: 'Brecheisen', worth: 55 });
    }
    g.restock(stock);
  }

  // ------------------------------------------------------------------ the House

  /** Runs every tick: thresholds, the bouncer, the boss clock, the chronicle. */
  private watch(): void {
    const sim = this.sim;
    const e = sim.economy;
    if (sim.over) return;

    this.chronicle.verdachtPeak = Math.max(this.chronicle.verdachtPeak, e.verdacht);

    /**
     * **THE HOUSE WATCHES DURING DIE LEHRNACHT; IT DOES NOT ACT.**
     *
     * VERDACHT still rises exactly as it always does, and the meter is on screen the whole
     * time — the player shoves a machine, sees the number move, and learns that the House
     * is counting. What does not happen is the House *spending* it: no ZINKEN installed on
     * cabinets nobody has explained, and above all no Rausschmeißer, who is a whole
     * encounter with its own rules arriving in a night that has not yet taught the four
     * verbs.
     *
     * This is not the tutorial being made easy. house-and-heat.md calls VERDACHT "the
     * antagonist *and* the difficulty system", and a tutorial has no difficulty system —
     * punishing a player with a mechanic they have not been shown teaches them that the
     * game is arbitrary, which is the one thing this arcade is not. Measured on the
     * obedient path: VERDACHT reached **68** on 180 Münzen, purely from the two shoves the
     * lesson itself asks for. The night was summoning the bouncer as a direct result of
     * doing what Der Croupier said.
     */
    if (!this.lehrnacht) {
      // 30 — Gezinkt: the House installs a rigging on a cabinet of its choice.
      if (e.verdacht >= VERDACHT_THRESHOLDS.gezinkt && this.installedZinken.length < 3) {
        const due = this.installedZinken.length;
        if (e.verdacht >= VERDACHT_THRESHOLDS.gezinkt + due * 20) this.installZinken();
      }

      // 60 — Der Rausschmeißer: a hostile Rütteln duel on a machine of the House's choice.
      if (e.verdacht >= VERDACHT_THRESHOLDS.rausschmeisser && !this.bouncer) this.summonBouncer();
      if (this.bouncer) this.tickBouncer();
    }

    // 90 — Hausverbot: your coins stop being accepted. A brake, not a wall.
    const verboten = e.hausverbot;
    if (verboten !== this.lastHausverbot) {
      this.lastHausverbot = verboten;
      sim.emit({ t: 'hausverbot', active: verboten });
    }

    if (this.bossActive) this.tickBoss();
  }

  private lastHausverbot = false;

  private installZinken(): void {
    const free = ZINKEN.filter((z) => !this.installedZinken.includes(z.id));
    if (free.length === 0) return;
    const z = free[Math.floor(this.sim.rng.float('house:zinken') * free.length)]!;
    const mod = z.modifier!;
    // scope it to a powered cabinet it can actually act on
    const allowed = mod.machines ?? ALL_MACHINES.filter((m) => this.sim.cabinet(m)?.powered !== false);
    const machine = allowed[Math.floor(this.sim.rng.float('house:zinken') * allowed.length)] ?? 'pachinko';
    this.sim.pipe.add({ ...mod, machines: [machine], discovered: false });
    this.installedZinken.push(z.id);
    this.sim.addItem(z.id);
    this.sim.emit({ t: 'zinkenInstalled', machine, zinken: z.id });
  }

  /**
   * Discovery: playing the cabinet once is enough, or you can spend GLÜCK to inspect it
   * from the aisle. Either way the crooked "?" becomes a printed rule with its counter.
   */
  discover(machine: MachineId): boolean {
    let found = false;
    for (const m of this.sim.pipe.list()) {
      if (m.source !== 'zinken' || m.discovered !== false) continue;
      if (m.machines && !m.machines.includes(machine)) continue;
      m.discovered = true;
      this.sim.pipe.clearBoard(machine);
      this.sim.emit({ t: 'zinkenDiscovered', machine, zinken: m.id });
      found = true;
    }
    return found;
  }

  private summonBouncer(): void {
    const powered = ALL_MACHINES.filter((m) => this.sim.cabinet(m)?.powered !== false);
    const machine = powered[Math.floor(this.sim.rng.float('house:bouncer') * powered.length)] ?? 'pachinko';
    this.bouncer = { machine, nextShove: this.sim.tick + Math.round(1.5 / DT), survived: 0 };
    this.sim.emit({ t: 'rausschmeisser', machine, phase: 'arrive' });
  }

  /**
   * He leans on the cabinet. Note what this is: he calls the same `ruetteln()` the player
   * calls, so your TILT meter really is shared with his — house-and-heat.md's description
   * is the implementation, not a metaphor.
   */
  private tickBouncer(): void {
    const b = this.bouncer!;
    const cab = this.sim.cabinet(b.machine);
    if (!cab) { this.bouncer = null; return; }
    if (this.sim.tick < b.nextShove) return;

    b.nextShove = this.sim.tick + Math.round(1.4 / DT);
    cab.ruetteln(1.2);
    this.sim.emit({ t: 'rausschmeisser', machine: b.machine, phase: 'shove' });
    b.survived++;

    if (cab.tilt01 >= 1) {
      // he wins: he takes your highest-value token
      const worst = this.sim.takeBestToken();
      this.sim.emit({ t: 'rausschmeisser', machine: b.machine, phase: 'lose' });
      if (worst) this.chronicle.confiscated.push(worst);
      this.bouncer = null;
      this.sim.economy.addVerdacht(-10, 'Rausschmeißer zufrieden', (e) => this.sim.emit(e));
      return;
    }
    if (b.survived >= 8) {
      this.sim.emit({ t: 'rausschmeisser', machine: b.machine, phase: 'win' });
      this.sim.economy.addVerdacht(-30, 'Rausschmeißer abgeschüttelt', (e) => this.sim.emit(e));
      this.bouncer = null;
    }
  }

  // ------------------------------------------------------------------ the Saalchef door

  /**
   * **THE SAALCHEF.** A real FLIPPER table (machines.md: bosses are pinball tables that
   * play back), and the boss law holds without one special case:
   *
   * - *its own Beutel* — `bossBag` below. He puts his own tokens on the table as physical
   *   obstacles, so your [Magnet] drags them and your [Schwer] ploughs them. His bag is
   *   played by the same grammar yours is.
   * - *its own modifiers* — each phase installs an ordinary `Modifier` on the pipeline,
   *   which is why it can be handed to you afterwards as a charm.
   * - *it spends GLÜCK against you* — he buys target hits back, and the spend is armed a
   *   beat early and emitted, because an invisible counter is rubber-banding and
   *   house-and-heat.md forbids it.
   * - *its signature rule is the trophy* — `boss.trophy` IS the phase rule, re-sourced.
   */
  private openBoss(): void {
    const floor = this.sim.economy.floor;
    if (this.sim.economy.tickets < this.sim.ticketQuota) return;
    const table = this.sim.flipper();
    if (!table) return;
    this.sim.economy.spendTickets(this.sim.ticketQuota);
    this.sim.emit({ t: 'quotaMet', floor, tickets: this.sim.economy.tickets });

    const boss = bossFor(floor);
    this.bossPhases = boss.phases;
    this.bossPhase = -1;
    this.bossBanked = 0;
    this.lastCoins = this.sim.economy.muenzen;
    // He arrives with luck to spend, and more of it the deeper you are.
    this.bossGlueck = 2 + floor;
    this.bossNextBag = this.sim.tick + Math.round(6 / DT);
    table.powered = true;
    table.broken = false;
    table.resetTargets();
    this.sim.phase = 'boss';
    this.sim.activeMachine = 'flipper';
    this.sim.spielerX = table.origin.x;
    this.bossActive = true;
    this.sim.emit({ t: 'bossStart', floor, boss: boss.name });
    this.nextBossPhase();
  }

  private nextBossPhase(): void {
    if (this.bossPhase >= 0) {
      const prev = this.bossPhases[this.bossPhase];
      if (prev) this.sim.pipe.remove(prev.rule.id);
    }
    this.bossPhase++;
    const p = this.bossPhases[this.bossPhase];
    if (!p) { this.endBoss(true); return; }
    this.sim.pipe.add(p.rule);
    this.bossBanked = 0;
    this.lastCoins = this.sim.economy.muenzen;
    /**
     * Ninety seconds a phase, not forty-five. A rule-gauntlet phase was a coin target you
     * chased on machines you already knew; a FLIPPER phase is a *ball*, and a ball that
     * drains takes seven seconds to set up again. Measured: at forty-five seconds a
     * competent bot got three plunges and one score, which is not a fight, it is a
     * countdown with a pinball table in front of it.
     */
    this.bossDeadline = this.sim.tick + Math.round(90 / DT);
    this.sim.emit({ t: 'bossPhase', phase: this.bossPhase + 1, rule: p.rule.ruleText ?? p.rule.id });
  }

  /**
   * The Saalchef, played out on the table.
   *
   * Phase progress is measured in **coins banked**, deliberately, because that is the one
   * currency two completely different builds can both produce and therefore the reason
   * the fight is beatable by more than one archetype:
   *
   *  - the **kinetic** build ([Gummi], [Magnet]) blitzes the drop-target banks — many
   *    cheap hits, fast, and the bank-clear jackpot pays the pot out;
   *  - the **patience** build ([Zins], [Kleber]) keeps ONE ball alive, cradles it on the
   *    bat to aim, and lets it ripen between hits — few hits, each worth a fortune.
   *
   * Both are verified by bots in test/fun.test.ts, which is the honest form of the
   * "beatable by two archetypes" claim.
   */
  private tickBoss(): void {
    const p = this.bossPhases[this.bossPhase];
    if (!p) return;
    const table = this.sim.flipper();
    const gained = this.sim.economy.muenzen - this.lastCoins;
    if (gained > 0) this.bossBanked += gained;
    this.lastCoins = this.sim.economy.muenzen;

    if (table && !table.broken) {
      // ---- he plays his own bag onto the table
      if (this.sim.tick >= this.bossNextBag) {
        this.bossNextBag = this.sim.tick + Math.round(9 / DT);
        const bag = bossBag(this.sim.economy.floor);
        const spec = bag[Math.floor(this.sim.rng.float('boss:bag') * bag.length)]!;
        // `spawnToken` assigns the real id; the spec's is ignored.
        table.dropHausmuenze({ ...spec, tags: [...spec.tags] });
      }
      // ---- and he spends GLÜCK when you are one target from a bank. Armed early and
      // emitted, so the player sees the hand on the scale before it presses.
      const left = 5 - table.targetsDown();
      if (left === 1 && this.bossGlueck > 0 && !table.denyPending()) {
        this.bossGlueck--;
        table.armDeny();
        this.sim.emit({ t: 'bossPhase', phase: this.bossPhase + 1, rule: 'Der Saalchef setzt GLÜCK gegen Sie.' });
      }
    }

    if (this.bossBanked >= p.target) { this.nextBossPhase(); return; }
    if (this.sim.tick >= this.bossDeadline) this.endBoss(false);
  }

  private endBoss(won: boolean): void {
    const floor = this.sim.economy.floor;
    const boss = bossFor(floor);
    for (const p of this.bossPhases) this.sim.pipe.remove(p.rule.id);
    this.bossPhases = [];
    this.bossPhase = -1;
    this.bossActive = false;
    const table = this.sim.flipper();
    if (table) {
      // clear the table: his coins and yours leave with the fight
      for (const t of this.sim.tokensIn('flipper')) this.sim.removeToken(t.id, 'lost');
      table.resetTargets();
      table.powered = false;
    }
    this.sim.activeMachine = null;
    this.sim.phase = 'aisle';
    this.sim.emit({ t: 'bossEnd', floor, boss: boss.name, won });
    if (won) {
      // Boss law: the trophy IS his signature rule, stolen. And beating a Saalchef zeroes
      // VERDACHT — the House pretends it never happened.
      const trophy = boss.trophy;
      this.sim.pipe.add(trophy);
      this.sim.addItem(trophy.id);
      this.sim.emit({ t: 'item', id: trophy.id, name: boss.name + '-Trophäe', gained: true });
      this.sim.economy.addVerdacht(-100, 'Saalchef geschlagen', (e) => this.sim.emit(e));
      this.abrechnung();
    } else {
      this.sim.economy.addVerdacht(10, 'Saalchef verloren', (e) => this.sim.emit(e));
    }
  }

  // ------------------------------------------------------------------ Die Abrechnung

  /**
   * Der Croupier with the ledger. Partial payment is taken in kind, in a deliberate order
   * — tokens first, then enchantments, then a cabinet goes AUSSER BETRIEB. That order is
   * the slope-not-cliff rule: a run bleeds out over about two failed Abrechnungen and
   * never dies in one surprise. The true fail state is having nothing left worth taking.
   */
  abrechnung(): void {
    const sim = this.sim;
    const floor = sim.economy.floor;
    const due = DEBT_CURVE[Math.min(DEBT_CURVE.length - 1, floor - 1)]!;
    sim.phase = 'abrechnung';

    const seizable: { id: string; worth: number }[] = [];
    // 1. the pusher pile: your bank is visible collateral (economy.md)
    const pile = sim.pile();
    if (pile) {
      const tokens = sim.tokensIn('muenzschieber');
      let bank = 0;
      for (const t of tokens) bank += t.faceValue;
      if (bank > 0) seizable.push({ id: 'Haufen im Münzschieber', worth: Math.round(bank * 0.5) });
    }
    // 2. enchanted tokens out of Der Beutel, cheapest first — he is polite about it
    for (const s of sim.beutelSpecs().filter((x) => x.tags.length > 0).sort((a, b) => a.faceValue - b.faceValue)) {
      seizable.push({ id: `Marke #${s.id}`, worth: Math.round(s.faceValue * 8) });
    }
    // 3. a cabinet
    for (const id of ALL_MACHINES) {
      const c = sim.cabinet(id);
      if (c && !c.broken) seizable.push({ id: `Automat ${id}`, worth: 400 });
    }

    const res = sim.economy.settle(due, seizable, (e) => sim.emit(e));
    // What DER CROUPIER took, as distinct from what the House took by other means. The
    // chronicle's `confiscated` also collects the Rausschmeißer's takings, and a receipt
    // that reads them as an Abrechnung can print "Als Pfand: Marke #1" directly above
    // "*** BEZAHLT ***" — which is what the Lehrnacht's first receipt actually did.
    this.lastSettlement = [...res.confiscated];
    for (const taken of res.confiscated) {
      this.chronicle.confiscated.push(taken);
      this.applyConfiscation(taken);
    }

    const paidInFull = res.paid >= due;
    this.chronicle.floorsCleared = floor;

    if (!paidInFull && this.nothingLeftToTake()) {
      this.finish(false);
      return;
    }
    if (floor >= this.floors) {
      this.finish(paidInFull);
      return;
    }
    sim.emit({ t: 'floorEnd', floor });
    this.startFloor(floor + 1);
  }

  private applyConfiscation(id: string): void {
    if (id.startsWith('Automat ')) {
      const m = id.slice('Automat '.length) as MachineId;
      const c = this.sim.cabinet(m);
      if (c) c.broken = true;
    } else if (id.startsWith('Marke #')) {
      this.sim.removeFromBeutel(Number(id.slice('Marke #'.length)));
    } else if (id.startsWith('Haufen')) {
      this.sim.drainPile(0.5);
    }
  }

  /**
   * The true fail state is not "you ran out of coins" — it is having nothing left worth
   * taking (core-loop.md). An empty Beutel qualifies: with no tokens you cannot stake, so
   * there is no way back. Every cabinet dark qualifies too.
   */
  private nothingLeftToTake(): boolean {
    const anyCabinet = ALL_MACHINES.some((m) => {
      const c = this.sim.cabinet(m);
      return c && !c.broken;
    });
    // nothing left worth taking: no cabinet still standing, or a bag of bare change
    return !anyCabinet || this.sim.bagIsBare();
  }

  // ------------------------------------------------------------------ the receipt

  /** Death is a receipt (core-loop.md). Win or lose, the run prints from a till roll. */
  private finish(won: boolean): void {
    const sim = this.sim;
    sim.over = true;
    sim.won = won;
    sim.phase = 'receipt';

    const c = this.chronicle;
    /**
     * DIE LEHRNACHT prints a *small* receipt, and that is the design rather than a
     * shortcut: the till roll at the end of a real run is four screens long because a
     * real run is forty minutes and the point of it is the ledger. Eight minutes of being
     * taught produces one slip, it says LEHRGELD across the top, and it goes in a frame.
     */
    if (this.lehrnacht) {
      const paid = won;
      const L0 = [
        'LEHRGELD',
        '································',
        'Eine Nacht Unterricht.',
        `Einsätze       ${c.stakes}`,
        `Gewonnen       ${fmt(c.totalWon)} Münzen`,
        `Marker         ${fmt(DEBT_CURVE[0]!)} Münzen`,
        '································',
        paid
          ? 'Der Marker ist beglichen.'
          : this.lastSettlement.length > 0
            ? `Als Pfand: ${this.lastSettlement[0]}`
            : 'Angeschrieben.',
        '································',
        paid ? '        *** BEZAHLT ***' : '        *** ANGESCHRIEBEN ***',
        'Der Croupier merkt sich das.',
      ];
      this.receiptLines = L0;
      sim.emit({ t: 'receipt', lines: L0 });
      sim.phase = 'over';
      return;
    }
    const L: string[] = [];
    L.push('SPIELHÖLLE — NACHTABRECHNUNG');
    L.push('································');
    L.push(`Etagen         ${c.floorsCleared} / ${this.floors}`);
    L.push(`Einsätze       ${c.stakes}`);
    L.push(`Gewonnen       ${fmt(c.totalWon)} Münzen`);
    L.push(`Tickets        ${fmt(sim.economy.tickets)}`);
    L.push('································');
    if (c.bestCombo) {
      // the best combo is named automatically from the tags that made it
      L.push('BESTE KOMBINATION');
      L.push(`  ${comboName(c.bestCombo.tags)}`);
      L.push(`  ${fmt(Math.round(c.bestCombo.value))} Münzen · ${c.bestCombo.machine}`);
    } else {
      L.push('BESTE KOMBINATION');
      L.push('  keine der Rede wert');
    }
    L.push('································');
    L.push('DÜMMSTER VERLUST');
    L.push(c.dumbestLoss
      ? `  ${c.dumbestLoss.what} (${fmt(Math.round(c.dumbestLoss.value))}, ${c.dumbestLoss.machine})`
      : '  Sie haben nichts Dummes getan.');
    L.push('································');
    L.push(`VERDACHT (Höchststand)  ${c.verdachtPeak}`);
    L.push(`Banken gesprengt        ${c.bankBreaks}`);
    if (c.grabbed.length > 0) L.push(`Am Greifer geholt       ${c.grabbed.join(', ')}`);
    if (c.confiscated.length > 0) {
      L.push('································');
      L.push('DER CROUPIER NAHM MIT');
      for (const x of c.confiscated.slice(0, 6)) L.push(`  ${x}`);
    }
    L.push('································');
    L.push(won ? '        *** BEZAHLT ***' : '        *** KASSIERT ***');
    L.push(won
      ? 'Sperrstunde. Die Türen gehen auf.'
      : 'Der Croupier behält ein Andenken.');

    this.receiptLines = L;
    sim.emit({ t: 'receipt', lines: L });
    sim.phase = 'over';
  }

  receipt(): string[] {
    return [...this.receiptLines];
  }

  // ------------------------------------------------------------------ chronicle

  private readEvent(e: SimEvent): void {
    const c = this.chronicle;
    {
      if (e.t === 'payout') {
        c.totalWon += e.muenzen;
        c.bestPayout = Math.max(c.bestPayout, e.muenzen);
      } else if (e.t === 'tilt') {
        c.shoves++;
        c.shovesThisFloor++;
      } else if (e.t === 'zinkenStolen') {
        c.zinkenStolen++;
      } else if (e.t === 'bossEnd') {
        if (e.won) c.bossesBeaten++;
      } else if (e.t === 'floorEnd') {
        // "a floor played without once shoving a cabinet" — an unlock condition, and a
        // real way to play: VERDACHT never rises if you keep your hands off the glass.
        if (c.shovesThisFloor === 0) c.cleanFloors++;
        c.shovesThisFloor = 0;
      } else if (e.t === 'score') {
        c.maxTagsOnToken = Math.max(c.maxTagsOnToken, e.tags.length);
        if (!c.bestCombo || e.value > c.bestCombo.value) {
          c.bestCombo = { value: e.value, tags: [...e.tags], machine: this.sim.machine, pocket: e.pocket };
        }
      } else if (e.t === 'bankBreak') {
        c.bankBreaks++;
      } else if (e.t === 'grab' && e.kept && e.prize) {
        c.grabbed.push(e.prize);
      } else if (e.t === 'bust') {
        const v = this.sim.economy.muenzen;
        if (!c.dumbestLoss || v > c.dumbestLoss.value) {
          c.dumbestLoss = { value: v, machine: e.machine, what: 'ins Bustloch gezielt' };
        }
      } else if (e.t === 'abrechnung' && e.confiscated.length > 0) {
        // The floor where the debt curve first beat you. economy.md promises a linear
        // build meets this by floor 4–5; test/fun.test.ts holds it to that.
        if (c.firstConfiscationFloor < 0) c.firstConfiscationFloor = this.sim.economy.floor;
        if (!c.dumbestLoss) {
          c.dumbestLoss = { value: e.due - e.paid, machine: 'muenzschieber', what: 'die Abrechnung verfehlt' };
        }
      }
    }
  }

  // ------------------------------------------------------------------ meta verbs

  /** Non-machine actions. They live in the replay because the run depends on them. */
  private meta(m: MetaVerb): void {
    const sim = this.sim;
    /**
     * **A finished run is finished.** Found on the last hand-played run before shipping:
     * after the receipt printed, `over` was true but nothing refused further input, so
     * entering a cabinet set `phase` back to `machine` and the till receipt — which the
     * shell shows by phase — was quietly dismissed by walking away from it. You could
     * play past the end of your own night. Death is a receipt (core-loop.md); it is not
     * a suggestion.
     */
    if (sim.over) return;
    switch (m.t) {
      case 'walk':
        sim.spielerX = clamp(m.x, -8, 72);
        break;
      case 'enter': {
        const c = sim.cabinet(m.machine);
        if (!c || c.broken || !c.powered) return;
        // route pressure: each re-entry raises this cabinet's buy-in. Die Dauerkarte
        // waives exactly one re-entry per floor, which is the whole prize.
        const cost = c.buyIn();
        if (sim.economy.hausverbot) {
          // 90+: coins are refused, tickets still work. The engine sputters.
          if (!sim.economy.spendTickets(Math.max(1, Math.round(cost / 2)))) return;
        } else if (!sim.economy.spendCoins(cost)) return;
        c.entries++;
        sim.activeMachine = m.machine;
        sim.phase = m.machine === 'greifer' ? 'shop' : 'machine';
        sim.spielerX = c.origin.x;
        // playing a cabinet once is enough to find what is wrong with it
        this.discover(m.machine);
        break;
      }
      case 'leave':
        if (sim.activeMachine) {
          const c = sim.cabinet(sim.activeMachine);
          if (c) c.kassieren();
        }
        this.collectShop();
        // MASCHINENBAU DECISION 11: the pusher bar only sweeps while you are AT the
        // cabinet. Leaving has to actually clear `activeMachine` or an unattended pusher
        // prints money all run and keeps 200 bodies awake for nothing.
        sim.activeMachine = null;
        sim.phase = 'aisle';
        break;
      case 'inspect':
        // GLÜCK buys information: inspect from the aisle without playing
        if (sim.economy.spendGlueck(1)) this.discover(m.machine);
        break;
      case 'brecheisen': {
        const i = this.bag.indexOf('item:brecheisen');
        if (i < 0) return;
        const target = sim.pipe.list().find(
          (x) => x.source === 'zinken' && x.discovered === true &&
            (!x.machines || x.machines.includes(m.machine)),
        );
        if (!target) return;
        this.bag.splice(i, 1);
        sim.removeItem('item:brecheisen');
        sim.pipe.remove(target.id);
        const zid = this.installedZinken.find((z) => target.id.startsWith(z));
        const charm = stolenCharm(zid ?? target.id);
        if (charm?.modifier) {
          sim.pipe.add(charm.modifier);
          sim.addItem(charm.id);
          this.bag.push(charm.id);
          sim.emit({ t: 'item', id: charm.id, name: charm.name, gained: true });
        }
        sim.emit({ t: 'zinkenStolen', machine: m.machine, zinken: target.id });
        break;
      }
      case 'buy':
        this.collectShop();
        break;
      case 'foto': {
        // FOTOAUTOMAT: one slot, one photograph. Costs tickets, like everything at a
        // wonder machine — it buys a future, not a debt payment.
        if (sim.wunder !== 'fotoautomat' || this.foto) return;
        if (!sim.economy.spendTickets(10)) return;
        const snap = sim.snapshotCabinet(m.machine);
        if (!snap) return;
        this.foto = snap;
        sim.fotoHeld = m.machine;
        sim.emit({ t: 'item', id: 'foto:' + m.machine, name: `Foto: ${m.machine}`, gained: true });
        break;
      }
      case 'einloesen': {
        if (!this.foto) return;
        const machine = this.foto.machine;
        sim.restoreCabinet(machine, this.foto);
        this.foto = null;
        sim.fotoHeld = null;
        sim.emit({ t: 'item', id: 'foto:' + machine, name: `Foto: ${machine}`, gained: false });
        break;
      }
      /**
       * DER WAHRSAGER. A prophecy is bought with TICKETS and becomes an ordinary charm on
       * the pipeline — printed on the Hausregeln board, attributable, inspectable. It is
       * not a secret: this house shows its math (economy.md), and that applies to the
       * fortune teller too.
       */
      case 'weissagung': {
        if (sim.wunder !== 'wahrsager') return;
        const which = this.wahrsagerCard[m.index];
        if (which === undefined) return;
        const p = this.prophecies[which];
        if (!p || this.boughtProphecies.includes(p.id)) return;
        if (!sim.economy.spendTickets(p.price)) return;
        this.boughtProphecies.push(p.id);
        sim.pipe.add(p.modifier);
        sim.addItem(p.modifier.id);
        this.bag.push(p.modifier.id);
        sim.emit({ t: 'item', id: p.modifier.id, name: p.name, gained: true });
        sim.wunderCard = this.wahrsagerCardText();
        break;
      }
      /**
       * **DIE LEHRNACHT, beat by beat.** The shell's director decides *when*; this decides
       * what it means, in exactly the same place and the same way as every other meta
       * verb. Ignored outside the Lehrnacht, so a hand-edited replay of an ordinary run
       * cannot use it to relight cabinets the House switched off.
       */
      case 'lehrbeat':
        if (this.lehrnacht && isLehrBeat(m.beat)) applyBeat(sim, m.beat, this);
        break;
      case 'abrechnung':
        this.abrechnung();
        break;
      case 'boss':
        this.openBoss();
        break;
      case 'descend':
        this.abrechnung();
        break;
    }
  }

  /**
   * `LehrHost`: put exactly these items on the Preistheke's floor, as wrapped parcels.
   *
   * It goes through the same `Greifer.restock` the House uses every floor, so a Lehrnacht
   * parcel is an ordinary physics body with an ordinary grip roll and an ordinary chute —
   * which is what makes the first thing a player ever wins a real win.
   */
  stockPreistheke(ids: readonly string[]): void {
    const g = this.sim.greifer();
    if (!g) return;
    g.restock(ids.map((id) => {
      const def = ITEMS[id];
      return { item: id, name: def ? def.name : id, worth: def?.rarity === 'rar' ? 40 : 20 };
    }));
  }

  /**
   * Deal tonight's card: three prophecies, drawn without replacement from the ones this
   * run has not already sold, on a labelled stream like every other House decision.
   */
  private dealWahrsager(): void {
    const free: number[] = [];
    for (let i = 0; i < this.prophecies.length; i++) {
      if (!this.boughtProphecies.includes(this.prophecies[i]!.id)) free.push(i);
    }
    const card: number[] = [];
    while (card.length < 3 && free.length > 0) {
      const k = Math.floor(this.sim.rng.float('floor:wahrsager') * free.length);
      card.push(free.splice(k, 1)[0]!);
    }
    this.wahrsagerCard = card;
  }

  /** What the machine prints, for the shell. Empty when there is no WAHRSAGER tonight. */
  private wahrsagerCardText(): { id: string; name: string; text: string; price: number }[] {
    if (this.sim.wunder !== 'wahrsager') return [];
    return this.wahrsagerCard
      .map((i) => this.prophecies[i]!)
      .filter((p) => !this.boughtProphecies.includes(p.id))
      .map((p) => ({ id: p.id, name: p.name, text: p.text, price: p.price }));
  }

  /** Take whatever fell down the chute into the bag, and make it real in the pipeline. */
  private collectShop(): void {
    const g = this.sim.greifer();
    if (!g) return;
    for (const prize of g.drainClaimed()) {
      const def = ITEMS[prize.item];
      if (!def) continue;
      this.bag.push(def.id);
      if (def.grantsTag) {
        // an enchantment is a tag applied to a token: enchant the least-enchanted one
        this.sim.enchantWeakest(def.grantsTag);
      } else if (def.modifier) {
        this.sim.pipe.add(def.modifier);
      }
      this.sim.addItem(def.id);
    }
  }

  items(): readonly string[] {
    return this.bag;
  }

  /** The floor on which the House first took payment in kind. -1 if it never had to. */
  firstConfiscationFloor(): number {
    return this.chronicle.firstConfiscationFloor;
  }

  /**
   * What the run did, for the meta layer. Read-only facts; the shell turns them into a
   * `RunSummary`. Nothing here feeds back into the sim — unlocks change *pools*, never
   * numbers, so a fact leaking the wrong way could not buy the player anything anyway.
   */
  chronicleSummary(): {
    verdachtPeak: number; bankBreaks: number; bossesBeaten: number; shoves: number;
    cleanFloors: number; bestPayout: number; bestComboTags: Tag[]; bestComboValue: number;
    maxTagsOnToken: number; zinkenStolen: number; grabbed: string[]; confiscated: string[];
  } {
    const c = this.chronicle;
    return {
      verdachtPeak: c.verdachtPeak,
      bankBreaks: c.bankBreaks,
      bossesBeaten: c.bossesBeaten,
      shoves: c.shoves,
      cleanFloors: c.cleanFloors,
      bestPayout: Math.round(c.bestPayout),
      bestComboTags: c.bestCombo ? [...c.bestCombo.tags] : [],
      bestComboValue: Math.round(c.bestCombo?.value ?? 0),
      maxTagsOnToken: c.maxTagsOnToken,
      zinkenStolen: c.zinkenStolen,
      grabbed: [...c.grabbed],
      confiscated: [...c.confiscated],
    };
  }
}

// ---------------------------------------------------------------- boss roster

interface BossDef { name: string; phases: BossPhase[]; trophy: Modifier }

/**
 * Saalchefs as rule gauntlets. Each phase installs one real modifier and asks for a coin
 * target inside 45 seconds; the trophy is that modifier, re-sourced as a charm. When wave
 * 4 replaces this with the FLIPPER tables, the trophies and the boss law stay put.
 */
function bossFor(floor: number): BossDef {
  if (floor >= 5) {
    return {
      name: 'Der Croupier',
      phases: [
        { rule: houseRule('boss:croupier-1', 'Der Croupier: die Etagenstufe zahlt nur die Hälfte.', { stageValue: (c, v) => (c.stage === 'floor' ? v * 0.5 : v) }), target: 260 },
        { rule: houseRule('boss:croupier-2', 'Der Croupier: die STRÄHNE zählt nicht mehr.', { stageValue: (c, v) => (c.stage === 'straehne' ? v * 0.6 : v) }), target: 420 },
        { rule: houseRule('boss:croupier-3', 'Der Croupier: alle Chancen laufen invertiert.', { odds: (_c, p) => 1 - p }), target: 700 },
      ],
      trophy: houseRule('trophy:croupier', 'Croupier-Trophäe: die Etagenstufe zahlt Ihnen anderthalbfach.', { stageValue: (c, v) => (c.stage === 'floor' ? v * 1.5 : v) }, 'charm'),
    };
  }
  if (floor >= 3) {
    return {
      name: 'Die Glücksfee',
      phases: [
        { rule: houseRule('boss:fee-1', 'Die Glücksfee: der Mitleidsmotor läuft trocken.', { odds: (_c, p) => p * 0.75 }), target: 150 },
        { rule: houseRule('boss:fee-2', 'Die Glücksfee: die Taschenmäuler ziehen sich zusammen.', { pocketWidth: (_c, v) => v * 0.7 }), target: 300 },
      ],
      trophy: houseRule('trophy:fee', 'Glücksfee-Trophäe: Ihre Taschenmäuler stehen ein Viertel weiter offen.', { pocketWidth: (_c, v) => v * 1.25 }, 'charm'),
    };
  }
  return {
    name: 'Der Buchhalter',
    phases: [
      { rule: houseRule('boss:buchhalter-1', 'Der Buchhalter: er besteuert die Materialstufe mit 30 %.', { stageValue: (c, v) => (c.stage === 'material' ? v * 0.7 : v) }), target: 90 },
      { rule: houseRule('boss:buchhalter-2', 'Der Buchhalter: und den Topf gleich mit.', { stageValue: (c, v) => (c.stage === 'topf' ? v * 0.7 : v) }), target: 190 },
    ],
    // phase 2 lets you re-order his tax stage into your combo (machines.md) — as a charm
    // his tax runs backwards and the material stage pays you a third more.
    trophy: houseRule('trophy:buchhalter', 'Buchhalter-Trophäe: die Materialstufe zahlt ein Drittel mehr.', { stageValue: (c, v) => (c.stage === 'material' ? v * 1.33 : v) }, 'charm'),
  };
}

/**
 * **Der Saalchef's own Beutel** (machines.md boss law). These are his tokens, put on his
 * table as physical obstacles — and because they are ordinary tokens carrying ordinary
 * tags, your build interacts with them for free: a [Magnet] ball drags his coins into
 * your lane, a [Schwer] ball ploughs straight through the clutter he is building, and his
 * [Kleber] coins genuinely become terrain you have to play around. He gets heavier and
 * stickier the deeper you go, which is the only difficulty curve he has.
 */
function bossBag(floor: number): TokenSpec[] {
  const base: TokenSpec[] = [
    { id: 9001, material: 'obsidian', faceValue: 0, radius: 0.5, tags: ['Schwer'] },
    { id: 9002, material: 'obsidian', faceValue: 0, radius: 0.5, tags: ['Kleber'] },
  ];
  if (floor >= 3) base.push({ id: 9003, material: 'obsidian', faceValue: 0, radius: 0.5, tags: ['Magnet'] });
  if (floor >= 5) base.push({ id: 9004, material: 'obsidian', faceValue: 0, radius: 0.5, tags: ['Schwer', 'Kleber'] });
  return base;
}

function houseRule(
  id: string,
  ruleText: string,
  queries: Modifier['queries'],
  source: Modifier['source'] = 'zinken',
): Modifier {
  return { id, source, tags: [], priority: 300, ruleText, discovered: true, queries };
}

// ---------------------------------------------------------------- receipt helpers

/**
 * "GEISTER-MAGNET-KASKADE, 4.812 Münzen" — the best combo names itself from the tags that
 * produced it (core-loop.md). No authored strings per combination; the grammar names its
 * own children, which is the only way this scales with the item pool.
 */
const TAG_WORD: Record<Tag, string> = {
  Magnet: 'MAGNET', Geist: 'GEISTER', Gummi: 'GUMMI', Kleber: 'KLEBE', Spalt: 'SPALT',
  Gold: 'GOLD', Fluch: 'FLUCH', Feuer: 'FEUER', Gluecks: 'GLÜCKS', Schwer: 'SCHWER',
  Zins: 'ZINS', Echo: 'ECHO',
};

export function comboName(tags: readonly Tag[]): string {
  if (tags.length === 0) return 'EHRLICHE ARBEIT';
  const words = tags.map((t) => TAG_WORD[t]);
  const tail = tags.length >= 3 ? 'KASKADE' : tags.length === 2 ? 'KOMBINATION' : 'ZUG';
  return `${words.join('-')}-${tail}`;
}

/**
 * German thousands dots (ART-BIBLE §5), hand-rolled. `toLocaleString('de-DE')` depends on
 * the host's ICU build, and the receipt is a run artefact people will compare — it has no
 * business varying between a browser and a CI container.
 */
function fmt(n: number): string {
  const v = Math.round(Math.abs(n)).toString();
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (i > 0 && (v.length - i) % 3 === 0) out += '.';
    out += v[i];
  }
  return (n < 0 ? '-' : '') + out;
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);

export type { HausregelDef, SetzenPayload, RunPhase };
